import React, { Fragment, useMemo } from "react";
import { useTranslationV3 } from "../../../i18nV3/useTranslationV3";
import styled from "styled-components";
import { formatDuration } from "../../../utils/durationUtils";
import { BoundRegularText } from "./Typography";

interface Props {
  duration: string;
  numberOfStops: number;
  lineThickness?: number;
  bigCircleDiameter?: number;
  smallCircleDiameter?: number;
  width?: number;
}

const StopsIndicator = ({
  numberOfStops,
  duration,
  lineThickness = 1,
  bigCircleDiameter = 9,
  smallCircleDiameter = 5,
  width,
}: Props) => {
  const { t } = useTranslationV3();

  const stopsLabel = useMemo(() => {
    if (numberOfStops === 0) {
      return t("direct");
    } else if (numberOfStops === 1) {
      return `${numberOfStops} ${t("stop")}`;
    } else {
      return `${numberOfStops} ${t("stops")}`;
    }
  }, [numberOfStops, t]);

  return (
    <MainContainer width={width}>
      <TotalFlightTime>{formatDuration(duration)}</TotalFlightTime>
      <StopsVisualization>
        <BigCircle lineThickness={lineThickness} diameter={bigCircleDiameter} />
        {[...Array(numberOfStops)].map((v, i) => (
          <Fragment key={i}>
            <ConnectionLine lineThickness={lineThickness} />
            <SmallCircle diameter={smallCircleDiameter}>
              <BlackCenter
                lineThickness={lineThickness}
                smallCircleDiameter={smallCircleDiameter}
              />
            </SmallCircle>
          </Fragment>
        ))}
        <ConnectionLine lineThickness={lineThickness} />
        <BigCircle diameter={bigCircleDiameter} lineThickness={lineThickness} />
      </StopsVisualization>
      <StopsNumber>{stopsLabel}</StopsNumber>
    </MainContainer>
  );
};

const MainContainer = styled.div<{ width?: number }>`
  width: ${({ width }) => (typeof width === "number" ? `${width}px` : "100%")};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;

const TotalFlightTime = styled(BoundRegularText)`
  padding-bottom: 3px;
`;

const StopsVisualization = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
`;

const BigCircle = styled.div<{ lineThickness: number; diameter: number }>`
  min-width: ${({ diameter, lineThickness }) =>
    `${diameter - 2 * lineThickness}`}px;
  max-width: ${({ diameter, lineThickness }) =>
    `${diameter - 2 * lineThickness}`}px;
  min-height: ${({ diameter, lineThickness }) =>
    `${diameter - 2 * lineThickness}`}px;
  max-height: ${({ diameter, lineThickness }) =>
    `${diameter - 2 * lineThickness}`}px;
  border-radius: ${({ diameter }) => `${diameter + 1}`}px;
  border: ${({ lineThickness }) => `${lineThickness}`}px solid
    ${({ theme }) => theme.redesign.primaryColor};
  background: ${({ theme }) => theme.redesign.defaultBackground};
`;

const ConnectionLine = styled.div<{ lineThickness: number }>`
  height: ${({ lineThickness }) => lineThickness}px;
  background: ${({ theme }) => theme.redesign.primaryColor};
  flex: 1;
`;

const SmallCircle = styled.div<{ diameter: number }>`
  min-width: ${({ diameter }) => diameter}px;
  max-width: ${({ diameter }) => diameter}px;
  min-height: ${({ diameter }) => diameter}px;
  max-height: ${({ diameter }) => diameter}px;
  border-radius: ${({ diameter }) => diameter}px;
  background: ${({ theme }) => theme.redesign.primaryColor};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BlackCenter = styled.div<{
  lineThickness: number;
  smallCircleDiameter: number;
}>`
  background: ${({ theme }) => theme.redesign.defaultColor};
  width: ${({ lineThickness, smallCircleDiameter }) =>
    `${smallCircleDiameter - 2 * lineThickness}`}px;
  height: ${({ lineThickness, smallCircleDiameter }) =>
    `${smallCircleDiameter - 2 * lineThickness}`}px;
  border-radius: ${({ lineThickness, smallCircleDiameter }) =>
    `${smallCircleDiameter - 2 * lineThickness}`}px;
`;

const StopsNumber = styled(BoundRegularText)``;

export default StopsIndicator;
